/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiBadge } from '../../badge';
import { EuiI18n } from '../../i18n';
import { EuiComboBoxOptionAppendPrepend } from '../utils';
export class EuiComboBoxPill extends Component {
  static defaultProps = {
    color: 'hollow'
  };
  onCloseButtonClick = () => {
    const {
      onClose,
      option
    } = this.props;
    if (onClose) {
      onClose(option);
    }
  };
  render() {
    const {
      children,
      className,
      color,
      onClick,
      onClickAriaLabel,
      onClose,
      option,
      ...rest
    } = this.props;
    const classes = classNames('euiComboBoxPill', className);
    const onClickProps = onClick && onClickAriaLabel ? {
      onClick,
      onClickAriaLabel
    } : {};
    const content = <EuiComboBoxOptionAppendPrepend option={option} classNamePrefix="euiComboBoxPill">
        {/* .euiBadge__text normally text truncates, but because we set it to flex to align prepend/append
          it breaks and we need to restore it manually
         */}
        <span className="eui-textTruncate">{children}</span>
      </EuiComboBoxOptionAppendPrepend>;
    if (onClose) {
      return <EuiI18n token="euiComboBoxPill.removeSelection" default="Remove {children} from selection in this group" values={{
        children
      }}>
          {removeSelection => <EuiBadge className={classes} color={color} data-test-subj="euiComboBoxPill" iconOnClick={this.onCloseButtonClick} iconOnClickAriaLabel={removeSelection} iconSide="right" iconType="cross" title={children} {...onClickProps} {...rest}>
              {content}
            </EuiBadge>}
        </EuiI18n>;
    }
    return <EuiBadge className={classes} color={color} data-test-subj="euiComboBoxPill" title={children} {...rest} {...onClickProps}>
        {content}
      </EuiBadge>;
  }
}
EuiComboBoxPill.propTypes = {
  children: PropTypes.string,
  className: PropTypes.string,
  color: PropTypes.string,
  onClick: PropTypes.func,
  onClickAriaLabel: PropTypes.any,
  onClose: PropTypes.func,
  option: PropTypes.shape({
    isGroupLabelOption: PropTypes.bool,
    label: PropTypes.string.isRequired,
    key: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      isGroupLabelOption: PropTypes.bool,
      label: PropTypes.string.isRequired,
      key: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.any.isRequired),
      value: PropTypes.any,
      prepend: PropTypes.node,
      append: PropTypes.node,
      truncationProps: PropTypes.any,
      className: PropTypes.string,
      "aria-label": PropTypes.string,
      "data-test-subj": PropTypes.string,
      css: PropTypes.any
    }).isRequired),
    value: PropTypes.any,
    prepend: PropTypes.node,
    append: PropTypes.node,
    truncationProps: PropTypes.any,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }).isRequired,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any
};
try {
  EuiComboBoxPill.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
    description: '',
    displayName: 'EuiComboBoxPill',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      color: {
        defaultValue: {
          value: 'hollow'
        },
        description: '',
        name: 'color',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onClick: {
        defaultValue: null,
        description: '',
        name: 'onClick',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        }],
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLButtonElement>'
        }
      },
      onClickAriaLabel: {
        defaultValue: null,
        description: '',
        name: 'onClickAriaLabel',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onClose: {
        defaultValue: null,
        description: '',
        name: 'onClose',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        }],
        required: false,
        type: {
          name: 'OptionHandler<T>'
        }
      },
      option: {
        defaultValue: null,
        description: '',
        name: 'option',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        }],
        required: true,
        type: {
          name: 'EuiComboBoxOptionOption<T>'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      }
    },
    extendedInterfaces: ['EuiComboBoxPillProps', 'CommonProps']
  };
} catch (e) {}